import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import env from "../../../../../config/env";
import { useStore } from "../../../../../store";
import Stars from "src/components/shared/stars";
import { useEffect, useState } from "react";

const CustomButtonGroup: React.FC<any> = ({ next, previous }) => {
  return (
    <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-between w-full transition-all duration-300">
      <div
        className={`flex items-center justify-center p-2.5 ml-2 bg-primary-gray-50 opacity-70 hover:opacity-100 rounded-full transition-all duration-300 cursor-pointer`}
        onClick={previous}
      >
        <img src={`/images/icons/arrow-left-purple1.svg`} />
      </div>
      <div className="w-full z-[1]"></div>
      <div
        className={`flex items-center justify-center p-2.5 mr-2 bg-primary-gray-50 opacity-70 hover:opacity-100 rounded-full transition-all duration-300 cursor-pointer`}
        onClick={next}
      >
        <img src={`/images/icons/arrow-right-purple1.svg`} />
      </div>
    </div>
  );
};

const ClassroomDetails = () => {
  const { courseStore } = useStore();

  const session: any = courseStore?.classDetails;

  const bookingRoom = session?.sessionBooking?.[0]?.bookingRoom;

  const location = bookingRoom?.address?.name;

  const images: any[] = bookingRoom?.album;
  if (!images?.some((image) => image?.name === bookingRoom?.logo?.name)) {
    images?.unshift(bookingRoom?.logo);
  }
  const rating = bookingRoom?.rating || 5;
  const ratingsCount = bookingRoom?.ratingsCount || 10;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  });

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: Math.min(images?.length, 3),
    },
    tablet: {
      breakpoint: { max: 1024, min: 640 },
      items: Math.min(images?.length, 3),
    },
    mobile: {
      breakpoint: { max: 640, min: 0 },
      items: Math.min(images?.length, 2),
    },
  };

  const getMeetingPlatform = () => {
    const link = session?.onlineSessionLink;

    if (!link) return null;

    const zoomPatterns = [
      /zoom\.us/
    ];

    const googleMeetPatterns = [
      /^https?:\/\/meet\.google\.com/,
      /^meet\.google\.com/,
    ];

    const teamsPatterns = [
      /^https?:\/\/teams\.microsoft\.com/,
      /^teams\.microsoft\.com/,
    ];

    if (googleMeetPatterns.some((pattern) => pattern.test(link))) {
      return {
        name: "Google Meet",
        logo: "/images/icons/socialMedia/GoogleMeet.svg",
      };
    } else if (teamsPatterns.some((pattern) => pattern.test(link))) {
      return {
        name: "Microsoft Teams",
        logo: "/images/icons/socialMedia/Teams.svg",
      };
    } else if (zoomPatterns.some((pattern) => pattern.test(link))) {
      return { name: "Zoom", logo: "/images/icons/socialMedia/Zoom.svg" };
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-2.5 px-5 sm:px-8 py-4 pb-[50px] sm:pb-[100px] bg-white">
      <div className="flex items-center gap-2.5 sm:gap-3 p-1.5 sm:p-2.5 bg-primary-purple-50 border border-primary-purple-100 rounded-[18px]">
        {session?.isOnline ? (
          <div className="flex items-center justify-center gap-3.5 sm:gap-4 w-full py-3.5 sm:py-2.5">
            <img src={getMeetingPlatform()?.logo} width={50} height={50} />
            <div>
              <div className="body-medium-bold">Online Class</div>
              <p className="body-small text-primary-gray-500">
                Hosted by {getMeetingPlatform()?.name}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center min-h-[73px] w-[73px] bg-primary-purple-100 rounded-[10px]">
              <img
                src="/images/icons/location-purple1.svg"
                width={28}
                height={26}
              />
            </div>
            <div>
              <div className="body-medium-bold">{location?.split(", ")[0]}</div>
              <p className="body-small text-primary-gray-500">{location}</p>
              <a
                href={`https://www.google.com/maps?q=${bookingRoom?.location?.lat},${bookingRoom?.location?.lng}`}
                target="_blank"
                className="flex items-center gap-1 body-small text-primary-purple-500 underline cursor-pointer"
              >
                View on Map
                <img src="/images/icons/explore-purple.svg" />
              </a>
            </div>
          </>
        )}
      </div>

      {!session?.isOnline && (
        <>
          {images?.length > 0 && (
            <div className="relative -mx-[3px] rounded-lg overflow-hidden">
              <Carousel
                responsive={responsive}
                arrows={false}
                infinite
                customButtonGroup={<CustomButtonGroup />}
              >
                {images?.map((image) => (
                  <div className="mx-[3px]" key={image?.name}>
                    {!loading ? (
                      <img
                        className="w-full h-[100px] object-cover rounded-lg"
                        src={
                          image?.name
                            ? `${env.IMAGE_API}${image.name}`
                            : "/images/course-default.png"
                        }
                      />
                    ) : (
                      <div className="h-[100px] bg-gray-300 rounded-[8px] animate-pulse"></div>
                    )}
                  </div>
                ))}
              </Carousel>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <h3 className="text-body-medium font-body-medium-bold sm:text-body-large sm:font-body-large">
              {bookingRoom?.name}
            </h3>
            <div className="flex items-center gap-2.5">
              <span className="caption-text-bold text-primary-gray-500">
                {rating}
              </span>
              <Stars rating={rating}></Stars>
              <span className="caption-text text-primary-gray-300">
                ({ratingsCount})
              </span>
            </div>
          </div>

          <p className="body-small">{bookingRoom?.description}</p>
        </>
      )}
    </div>
  );
};

export default ClassroomDetails;
